// Targeting policy scoring and Focus Aim overlay.
// Conforms to GDD §5 and §6.3.1.

import { cfg } from '../config.js';
import type { WeaponState, Enemy, Vec2, WeaponDef } from '../types.js';
import type { WorldState } from './state.js';
import weaponsData from '../content/weapons.json';
import { wrappedDelta } from './collision.js';

const WEAPON_DEFS = (weaponsData as { version: string; entries: WeaponDef[] }).entries;

const EPS = 1e-6;
const FOCUS_CONE_RAD = (30 * Math.PI) / 180;
const FORWARD_CONE_RAD = (60 * Math.PI) / 180;
const PREDICT_MAX_TIME = 0.5;

export interface TargetResult {
  targetId: number;
  aimPoint: Vec2;
}

interface Candidate {
  enemy: Enemy;
  d: number;
  score: number;
}

const acquiredAt = new WeakMap<WeaponState, number>();

function getWeaponDef(id: string): WeaponDef | undefined {
  return WEAPON_DEFS.find((d) => d.id === id);
}

function len(v: Vec2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

function normalize(v: Vec2): Vec2 {
  const l = len(v);
  if (l < EPS) return { x: 0, y: 1 };
  return { x: v.x / l, y: v.y / l };
}

function dist(a: Vec2, b: Vec2): number {
  const d = wrappedDelta(a, b);
  return Math.sqrt(d.x * d.x + d.y * d.y);
}

function angleBetween(a: Vec2, b: Vec2): number {
  const la = len(a);
  const lb = len(b);
  if (la < EPS || lb < EPS) return 0;
  let cos = (a.x * b.x + a.y * b.y) / (la * lb);
  cos = Math.max(-1, Math.min(1, cos));
  return Math.acos(cos);
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function isBoss(e: Enemy): boolean {
  return e.boss !== undefined;
}

function isFocusWeapon(weapon: WeaponState): boolean {
  // Gate 1: the first equipped weapon is the focus weapon.
  return weapon.slot === 0;
}

function acquisitionRange(weapon: WeaponState, world: WorldState): number {
  const def = getWeaponDef(weapon.def_id);
  // Never acquire beyond the visible circle: the ship doesn't attack enemies
  // the player can't see (world.viewRadius is pushed by the renderer).
  return Math.min(def?.range ?? 20, world.viewRadius);
}

function projectileSpeed(weapon: WeaponState): number {
  const def = getWeaponDef(weapon.def_id);
  return def?.speed ?? 44;
}

export function predictAim(source: Vec2, targetPos: Vec2, targetVel: Vec2, vp: number): Vec2 {
  const toTarget = wrappedDelta(targetPos, source);
  const d = Math.sqrt(toTarget.x * toTarget.x + toTarget.y * toTarget.y);
  if (d < EPS || vp < EPS) {
    return { x: targetPos.x, y: targetPos.y };
  }

  const t0 = d / vp;
  const p1x = targetPos.x + targetVel.x * t0;
  const p1y = targetPos.y + targetVel.y * t0;

  const toPredicted = wrappedDelta({ x: p1x, y: p1y }, source);
  const d1 = Math.sqrt(toPredicted.x * toPredicted.x + toPredicted.y * toPredicted.y);
  const t1 = Math.max(0, Math.min(d1 / vp, PREDICT_MAX_TIME));

  return { x: targetPos.x + targetVel.x * t1, y: targetPos.y + targetVel.y * t1 };
}

function headingForPolicy(player: WorldState['player']): Vec2 {
  const speed = len(player.vel);
  if (speed >= 0.5) {
    return normalize(player.vel);
  }
  const aim = player.focus_aim.dir;
  if (len(aim) < EPS) return { x: 0, y: 1 };
  return normalize(aim);
}

function scoreCandidate(
  candidate: Enemy,
  policy: string,
  ctx: {
    source: Vec2;
    R: number;
    heading: Vec2;
    focusDir: Vec2;
  }
): number {
  const to = { x: candidate.pos.x - ctx.source.x, y: candidate.pos.y - ctx.source.y };
  const d = len(to);
  const fD = 1 - d / ctx.R;

  switch (policy) {
    case 'nearest': {
      return fD;
    }

    case 'forward-cone': {
      const theta = angleBetween(ctx.heading, to);
      const fF = clamp01(1 - theta / ((45 * Math.PI) / 180));
      return 0.6 * fF + 0.4 * fD;
    }

    case 'player-selected': {
      const theta = angleBetween(ctx.focusDir, to);
      if (theta > FOCUS_CONE_RAD + EPS) return -Infinity;
      const fAim = clamp01(1 - theta / FOCUS_CONE_RAD);
      const fB = isBoss(candidate) ? 1 : 0;
      const fE = candidate.elite ? 1 : 0;
      return 3.0 * fAim + 2.0 * fB + 1.0 * fE + 0.5 * fD;
    }

    default: {
      return fD;
    }
  }
}

function currentTargetValid(
  weapon: WeaponState,
  world: WorldState,
  tick: number
): Enemy | null {
  if (weapon.last_target_id <= 0) return null;
  const entity = world.getEntityById(weapon.last_target_id);
  if (!entity || !entity.alive) return null;
  const enemy = entity as Enemy;
  if (!enemy.targetable) return null;
  if (enemy.telegraph_until > tick) return null;
  const R = acquisitionRange(weapon, world);
  if (dist(world.player.pos, enemy.pos) > R * 1.1 + EPS) return null;
  return enemy;
}

function gatherCandidates(
  weapon: WeaponState,
  world: WorldState,
  tick: number,
  policy: string,
  requireInCone: boolean
): Candidate[] {
  const R = acquisitionRange(weapon, world);
  const source = world.player.pos;
  const heading = headingForPolicy(world.player);
  const focusDir = normalize(world.player.focus_aim.dir);
  const out: Candidate[] = [];

  for (const enemy of world.enemies.alive()) {
    if (!enemy.alive) continue;
    if (!enemy.targetable) continue;
    if (enemy.telegraph_until > tick) continue;

    const d = dist(source, enemy.pos);
    if (d > R + EPS) continue;

    if (requireInCone && policy === 'player-selected') {
      const to = { x: enemy.pos.x - source.x, y: enemy.pos.y - source.y };
      const theta = angleBetween(focusDir, to);
      if (theta > FOCUS_CONE_RAD + EPS) continue;
    }

    const score = scoreCandidate(enemy, policy, { source, R, heading, focusDir });
    if (Number.isFinite(score)) {
      out.push({ enemy, d, score });
    }
  }

  return out;
}

function pickBest(candidates: Candidate[]): Candidate | null {
  if (candidates.length === 0) return null;
  // Iterate in ascending entity ID so ties break to the lowest ID.
  let best = candidates[0];
  for (let i = 1; i < candidates.length; i++) {
    const c = candidates[i];
    if (c.score > best.score + EPS) {
      best = c;
    } else if (Math.abs(c.score - best.score) <= EPS && c.enemy.id < best.enemy.id) {
      best = c;
    }
  }
  return best;
}

function reacquireInterval(weapon: WeaponState, world: WorldState): number {
  const focusHeld = world.player.focus_aim.held && isFocusWeapon(weapon);
  return focusHeld
    ? cfg.getNumber('targeting.focus_reacquire_interval')
    : cfg.getNumber('targeting.reacquire_interval');
}

function shouldReacquire(
  weapon: WeaponState,
  world: WorldState,
  tick: number,
  current: Enemy | null
): boolean {
  if (weapon.last_target_id <= 0) return true;
  if (!current) return true;

  const interval = reacquireInterval(weapon, world);
  return (tick + weapon.slot) % interval === 0;
}

function selectTarget(
  weapon: WeaponState,
  world: WorldState,
  tick: number,
  policy: string,
  requireInCone: boolean
): Candidate | null {
  const candidates = gatherCandidates(weapon, world, tick, policy, requireInCone);

  // Forward-cone: if the 60° prefilter is empty, evaluate the full circle.
  if (policy === 'forward-cone' && requireInCone && candidates.length === 0) {
    return pickBest(gatherCandidates(weapon, world, tick, policy, false));
  }

  return pickBest(candidates);
}

export function evaluateTarget(
  weapon: WeaponState,
  world: WorldState,
  tick: number
): TargetResult | null {
  const R = acquisitionRange(weapon, world);
  if (R <= 0) return null;

  const current = currentTargetValid(weapon, world, tick);
  const policy = weapon.policy;
  const overlayRequested =
    world.player.focus_aim.held && (isFocusWeapon(weapon) || policy === 'player-selected');

  if (!shouldReacquire(weapon, world, tick, current)) {
    if (current) {
      return {
        targetId: current.id,
        aimPoint: predictAim(world.player.pos, current.pos, current.vel, projectileSpeed(weapon)),
      };
    }
    return null;
  }

  const fallbackPolicy = policy === 'player-selected' ? 'nearest' : policy;
  const fallbackTarget = selectTarget(weapon, world, tick, fallbackPolicy, false);
  const overlayTarget = overlayRequested
    ? selectTarget(weapon, world, tick, 'player-selected', true)
    : null;

  let chosen: Candidate | null = fallbackTarget;

  if (overlayTarget && fallbackTarget) {
    const overlayPriority = isBoss(overlayTarget.enemy) || overlayTarget.enemy.elite;
    const fallbackPriority = isBoss(fallbackTarget.enemy) || fallbackTarget.enemy.elite;
    if (overlayPriority || !fallbackPriority) {
      chosen = overlayTarget;
    }
  } else if (overlayTarget) {
    chosen = overlayTarget;
  }

  const useOverlay = overlayRequested && chosen === overlayTarget;

  // Hysteresis against the current target.
  if (current && chosen && chosen.enemy.id !== current.id) {
    const source = world.player.pos;
    const heading = headingForPolicy(world.player);
    const focusDir = normalize(world.player.focus_aim.dir);
    const currentScore = scoreCandidate(current, useOverlay ? 'player-selected' : fallbackPolicy, {
      source,
      R,
      heading,
      focusDir,
    });
    const challengerScore = chosen.score;

    const isOverlay = useOverlay;
    const margin = isOverlay ? 0.1 : cfg.getNumber('targeting.hysteresis_margin');
    const minHold = isOverlay ? 0 : cfg.getNumber('targeting.min_hold_ticks');
    const acquiredTick = acquiredAt.get(weapon) ?? tick;
    const heldLongEnough = tick - acquiredTick >= minHold;

    const switchThreshold = currentScore * (1 + margin);
    if (!(challengerScore > switchThreshold + EPS && heldLongEnough)) {
      chosen = { enemy: current, d: dist(source, current.pos), score: currentScore };
    }
  }

  if (chosen) {
    if (weapon.last_target_id !== chosen.enemy.id) {
      acquiredAt.set(weapon, tick);
      weapon.last_target_id = chosen.enemy.id;
    }
    return {
      targetId: chosen.enemy.id,
      aimPoint: predictAim(world.player.pos, chosen.enemy.pos, chosen.enemy.vel, projectileSpeed(weapon)),
    };
  }

  if (weapon.last_target_id !== -1) {
    acquiredAt.set(weapon, tick);
    weapon.last_target_id = -1;
  }
  return null;
}
